import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames/bind';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import commaNumber from 'comma-number';
import moment from 'moment';
import { Select, Button, Row, Space } from 'antd';

import API from 'helpers/API';
import { getOrderDisplayNo, getOrderStatus, getPaymentMethod } from 'utils/CodeMapper';
import { getDisplayName } from 'utils/getDisplayName';
import { downloadExcelFile } from 'utils/FileDownloader';
import { showLoading } from 'store/loadingSlice';
import { HeadingH1, UnitTable } from 'components/ui';

import styles from './index.module.scss';

const cx = classNames.bind(styles);

const sortText = (a, b) => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  return String(a).localeCompare(String(b));
};

const sortNumber = (a, b) => Number(a ?? 0) - Number(b ?? 0);

const sortDateTime = (a, b) => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  return new Date(a).getTime() - new Date(b).getTime();
};

const Page = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState();
  const [filters, setFilters] = useState({ orderStatus: searchParams.get('orderStatus') ?? '' });

  useEffect(() => {
    search();
  }, [location]);

  const handleChangeFilter = (info) => {
    setFilters({
      ...filters,
      orderStatus: info.value,
    });
  };

  const searchWithParams = () => {
    const nextSearchParams = {};
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== '') {
        nextSearchParams[key] = filters[key];
      }
    });

    setSearchParams(nextSearchParams);
  };

  const search = () => {
    dispatch(showLoading(true));

    // API: 주문 내역 조회
    API.get('/orders')
      .then(({ success, data }) => {
        if (success) {
          setOrders(data.orders);
        }
      })
      .finally(() => dispatch(showLoading(false)));
  };

  const download = () => {
    const records = (filteredOrders ?? []).map((order) => {
      return {
        A: getOrderDisplayNo(order),
        B: order.orderDate ? moment(order.orderDate, 'YYYYMMDD').format('YYYY-MM-DD') : '',
        C: order.deliveredAt ? moment(order.deliveredAt).format('YYYY-MM-DD') : '',
        D: getOrderStatus(order.status),
        E: order.paymentMethod ? getPaymentMethod(order.paymentMethod) : '',
        F: order.quantity,
        G: order.customerName ?? '',
        H: order.deliveryName ?? '',
        I: [order.deliveryAddress, order.deliveryAddressDetail].filter(Boolean).join(' '),
        J: getDisplayName(order.createdBy),
        K: order.createdAt ? moment(order.createdAt).format('YYYY-MM-DD HH:mm:ss') : '',
        L: getDisplayName(order.updatedBy),
        M: order.updatedAt ? moment(order.updatedAt).format('YYYY-MM-DD HH:mm:ss') : '',
      };
    });

    downloadExcelFile(
      [
        {
          A: '주문번호',
          B: '주문일자',
          C: '납품일자',
          D: '상태',
          E: '결제 방법',
          F: '수량',
          G: '주문자명',
          H: '수신자명',
          I: '주소',
          J: '등록자',
          K: '등록일시',
          L: '수정자',
          M: '수정일시',
        },
      ],
      records,
      `주문내역-${moment().format('YYYYMMDDHHmmss')}`,
    );
  };

  const columns = [
    {
      key: 'orderNo',
      dataIndex: 'orderNo',
      title: '주문번호',
      sorter: (a, b) => sortText(getOrderDisplayNo(a), getOrderDisplayNo(b)),
      render: (_, item) => getOrderDisplayNo(item),
      width: 132,
    },
    {
      key: 'orderDate',
      dataIndex: 'orderDate',
      title: '주문일자',
      sorter: (a, b) => sortText(a.orderDate, b.orderDate),
      render: (_, item) => moment(item.orderDate, 'YYYYMMDD').format('YYYY-MM-DD'),
      width: 116,
      align: 'center',
    },
    {
      key: 'deliveredAt',
      dataIndex: 'deliveredAt',
      title: '납품일자',
      sorter: (a, b) => sortDateTime(a.deliveredAt, b.deliveredAt),
      render: (_, item) => (item.deliveredAt ? moment(item.deliveredAt).format('YYYY-MM-DD') : '-'),
      width: 116,
      align: 'center',
    },
    {
      key: 'status',
      dataIndex: 'status',
      title: '상태',
      sorter: (a, b) => sortText(getOrderStatus(a.status), getOrderStatus(b.status)),
      render: (_, item) => getOrderStatus(item.status),
      width: 92,
      align: 'center',
    },
    {
      key: 'paymentMethod',
      dataIndex: 'paymentMethod',
      title: '결제 방법',
      sorter: (a, b) => sortText(getPaymentMethod(a.paymentMethod), getPaymentMethod(b.paymentMethod)),
      render: (_, item) => getPaymentMethod(item.paymentMethod) || '-',
      width: 128,
      align: 'center',
    },
    {
      key: 'quantity',
      dataIndex: 'quantity',
      title: '수량',
      sorter: (a, b) => sortNumber(a.quantity, b.quantity),
      render: (_, item) => item.quantity,
      width: 72,
      align: 'center',
    },
    {
      key: 'customerName',
      dataIndex: 'customerName',
      title: '주문자명',
      sorter: (a, b) => sortText(a.customerName, b.customerName),
      render: (_, item) => <div className={cx('ellipsis')}>{item.customerName}</div>,
      width: 112,
    },
    {
      key: 'deliveryName',
      dataIndex: 'deliveryName',
      title: '수신자명',
      sorter: (a, b) => sortText(a.deliveryName, b.deliveryName),
      render: (_, item) => <div className={cx('ellipsis')}>{item.deliveryName}</div>,
      width: 112,
    },
    {
      key: 'deliveryAddress',
      dataIndex: 'deliveryAddress',
      title: '주소',
      ellipsis: true,
      sorter: (a, b) =>
        sortText(
          [a.deliveryAddress, a.deliveryAddressDetail].filter(Boolean).join(' '),
          [b.deliveryAddress, b.deliveryAddressDetail].filter(Boolean).join(' '),
        ),
      width: 220,
      render: (_, item) => [item.deliveryAddress, item.deliveryAddressDetail].filter(Boolean).join(' '),
    },
    {
      key: 'createdBy',
      dataIndex: 'createdBy',
      title: '등록자',
      sorter: (a, b) => sortText(getDisplayName(a.createdBy), getDisplayName(b.createdBy)),
      render: (_, item) => getDisplayName(item.createdBy),
      width: 88,
      align: 'center',
    },
    {
      key: 'createdAt',
      dataIndex: 'createdAt',
      title: '등록일시',
      sorter: (a, b) => sortDateTime(a.createdAt, b.createdAt),
      render: (_, item) => moment(item.createdAt).format('YYYY-MM-DD HH:mm:ss'),
      width: 156,
      align: 'center',
    },
    {
      key: 'updatedBy',
      dataIndex: 'updatedBy',
      title: '수정자',
      sorter: (a, b) => sortText(getDisplayName(a.updatedBy), getDisplayName(b.updatedBy)),
      render: (_, item) => getDisplayName(item.updatedBy),
      width: 88,
      align: 'center',
    },
    {
      key: 'updatedAt',
      dataIndex: 'updatedAt',
      title: '수정일시',
      sorter: (a, b) => sortDateTime(a.updatedAt, b.updatedAt),
      render: (_, item) => moment(item.updatedAt).format('YYYY-MM-DD HH:mm:ss'),
      width: 156,
      align: 'center',
    },
  ];

  const filteredOrders = useMemo(() => {
    if (orders === undefined) return;

    return orders.filter((order) => {
      const orderStatus = searchParams.get('orderStatus') ?? '';

      if (orderStatus !== '') {
        return order.status === orderStatus;
      }

      return true;
    });
  }, [orders, location]);

  return (
    <div className={cx('container')}>
      <HeadingH1 title={'주문 관리'} />

      <Row align={'middle'} justify={'space-between'}>
        <Space>
          <div className={cx('total')}>{filteredOrders && `총 ${commaNumber(filteredOrders.length)}`}건</div>
          <Select
            name="orderStatus"
            defaultValue={''}
            onChange={(v, info) => handleChangeFilter(info)}
            options={[
              { label: '전체상태', value: '' },
              { label: '주문접수', value: 'RECEIVED' },
              { label: '준비중', value: 'CHARGING' },
              { label: '배송중', value: 'SHIPPING' },
              { label: '배송완료', value: 'DELIVERED' },
              { label: '주문취소', value: 'CANCELED' },
            ]}
            style={{ width: 120 }}
          />
          <Button type="primary" onClick={searchWithParams}>
            조회
          </Button>
        </Space>
        <Space>
          <Button type="primary" onClick={download}>
            엑셀 다운로드
          </Button>
          <Button key={'addNewOrder'} type="primary" onClick={() => navigate('/orders/new')}>
            신규
          </Button>
        </Space>
      </Row>

      <UnitTable
        columns={columns}
        onRow={(record) => {
          return {
            onClick: () => navigate(`/orders/${record.rid}`),
          };
        }}
        dataSource={filteredOrders}
        rowKey={'rid'}
      />
      <div />
    </div>
  );
};

export default Page;
